// ============================================================================
// ENGAGEMENT TYPE SERVICE
// ============================================================================
// Business logic and HTTP calls for the EngagementType master.
//
// Backend wraps every response in ApiResponse<T> with this shape:
//   { success, message, data, errors, correlationId, timestamp }
//
// List endpoints additionally wrap data in PagedResult<T>:
//   data: { items, page, pageSize, totalCount, totalPages, hasPrevious, hasNext }
//
// The frontend grids (SyutGrid) consume PaginatedResponse<T> with shape:
//   { success, message, data: T[], pagination: { currentPage, pageSize, totalPages, totalItems, hasNextPage, hasPreviousPage } }
//
// `getAll()` MUST do this transformation — see CLAUDE.md "Service layer transformation".

import { get, post, put, del } from '../client/axiosClient';
import { engagementTypeEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  EngagementType,
  CreateEngagementTypeRequest,
  UpdateEngagementTypeRequest,
  EngagementTypeSearchRequest,
} from '../types';

class EngagementTypeService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: EngagementTypeSearchRequest
  ): Promise<PaginatedResponse<EngagementType>> {
    // Use raw BASE_PATH (NOT engagementTypeEndpoints.list()) — buildEndpointWithQuery
    // adds the /v1 prefix internally. Passing a pre-prefixed endpoint causes /v1/v1/...
    const endpoint = params
      ? buildEndpointWithQuery(engagementTypeEndpoints.basePath(), params as Record<string, any>)
      : engagementTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data; // ApiResponse<PagedResult<EngagementType>>
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<EngagementType>> {
    const response = await get<ApiResponse<EngagementType>>(
      engagementTypeEndpoints.getById(id)
    );
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<EngagementType>> {
    const response = await get<ApiResponse<EngagementType>>(
      engagementTypeEndpoints.getByCode(code)
    );
    return response.data;
  }

  async create(
    data: CreateEngagementTypeRequest
  ): Promise<ApiResponse<EngagementType>> {
    const response = await post<ApiResponse<EngagementType>>(
      engagementTypeEndpoints.create(),
      data
    );
    return response.data;
  }

  async update(
    id: ID,
    data: UpdateEngagementTypeRequest
  ): Promise<ApiResponse<EngagementType>> {
    const response = await put<ApiResponse<EngagementType>>(
      engagementTypeEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * DELETE returns 204 No Content (no response body) on success.
   * Returns 404 if the record doesn't exist.
   */
  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(engagementTypeEndpoints.delete(id));
    return { success: true };
  }
}

export const engagementTypeService = new EngagementTypeService();
export default engagementTypeService;
